<template>
  <main class="info">
    <div class="info__wrapper">
      <section class="info__description">
        <h2 v-show-text>
          {{currentData.main}}
        </h2>
      </section>
      <grid-section
        class="info__grid"
        :column-data="currentData.firstSection"/>
      <figure
        v-simple-parallax
        v-lazy-loading
        class="info__main-img">
        <img
          :data-src="currentData.mainImg"
          src=""
          alt="">
      </figure>
      <grid-section
        inverted
        class="info__grid"
        :column-data="currentData.secondSection"
      />
      <grid-section-single v-if="currentData.thirdSection" :column-data="currentData.thirdSection" />
      <contact-cta class="info__contact"/>
      <arrow
        inverted
        class="info__next" @click.native="pushNextPage()"
        :text="nextChapter"
      />
    </div>
  </main>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import anime from 'animejs';
import PagesData from '@/assets/data/pagesData';

// MEDUSA AND ITS TARGET
import medusa from '@/scripts/medusa';
import lazyLoading from '@/scripts/targets-medusa/lazyLoading';
import parallax from '@/scripts/targets-medusa/parallax';
import parallaxText from '@/scripts/targets-medusa/parallaxText';
import simpleParallax from '@/scripts/targets-medusa/simpleParallax';
import showText from '@/scripts/targets-medusa/showText';

import Arrow from '@/components/AppArrow.vue';
import GridSection from '@/components/GridSection.vue';
import GridSectionSingle from '@/components/GridSectionSingle.vue';
import ContactCta from '@/components/ContactCTA.vue';
import parallaxTitle from '@/scripts/targets-medusa/parallaxTitle';


export default {
  name: 'Info',
  components: {
    GridSectionSingle,
    ContactCta,
    GridSection,
    Arrow,
  },
  data() {
    return {
      data: PagesData,
    };
  },
  computed: {
    ...mapState([
      'blobInTransition',
      'loaderIsOpen',
      'heroIsCreated',
    ]),
    ...mapGetters([
      'getCurrentPage',
      'getNextPage',
    ]),
    nextChapter() {
      return `chapter ${this.getNextPage.chapter}`;
    },
    currentData() {
      return PagesData[this.getCurrentPage.reference];
    },
  },
  methods: {
    ...mapMutations([
      'goNextPage',
      'toggleSlideTransition',
    ]),
    pushNextPage() {
      this.$router.push(this.getNextPage.path);
      this.goNextPage();

      anime({
        targets: '.info__next',
        opacity: 0,
        duration: 300,
        easing: 'linear',
      });
    },
    titleAnimation(done = false, reverse = false) {
      const title = document.getElementsByClassName('title')[0];
      const staggerElement = title.getElementsByClassName('stagger-element');
      anime.remove(staggerElement);

      return anime({
        targets: staggerElement,
        autoplay: false,
        opacity: {
          value: reverse ? 0 : [0, 1],
          duration: 1000,
          delay: reverse ? 500 : 1000,
          easing: () => (!reverse ? 'easeInCubic' : 'easeOutCubic'),
        },
        translateY: {
          value: () => (
            reverse
              ? [0, '-90%']
              : ['90%', 0]
          ),
          duration: () => anime.random(1100, 1200),
          delay: () => (reverse ? anime.random(0, 400) : anime.random(0, 400) + 1000),
          easing: () => (!reverse ? 'easeOutCubic' : 'easeInCubic'),
        },
        complete: () => {
          if (done) {
            this.toggleSlideTransition(false);
          } else {
            const animation = this.titleAnimation(true, false);
            animation.play();
          }
        },
      });
    },
  },
  beforeRouteLeave(to, from, next) {
    if (from.name === 'Imagine' && to.name === 'Home') {
      this.toggleSlideTransition(true);
      const animation = this.titleAnimation(false, true);
      animation.play();
    }
    next();
  },
  created() {
    medusa.ref.addTarget(lazyLoading);
    medusa.ref.addTarget(parallax);
    medusa.ref.addTarget(parallaxText);
    medusa.ref.addTarget(simpleParallax);
    medusa.ref.addTarget(showText);

    if (this.heroIsCreated) {
      medusa.ref.addTarget(parallaxTitle);
    } else {
      this.$eventHub.$once('heroMounted', () => {
        medusa.ref.addTarget(parallaxTitle);
      });
    }
  },
  destroyed() {
    medusa.ref.removeTarget('lazyLoading');
    medusa.ref.removeTarget('parallax');
    medusa.ref.removeTarget('parallaxText');
    medusa.ref.removeTarget('parallaxTitle');
    medusa.ref.removeTarget('simpleParallax');
    medusa.ref.removeTarget('showText');
  },
};
</script>

<style scoped lang="scss">
$margin-top: 20vw;
$margin-top-tablet: 20vw;
$margin-top-mobile: 30vw;

$horizontal-padding-mobile-s: 10vw;
$horizontal-padding-mobile: 13.6vw;

.info {
  &__grid,
  &__main-img {
    padding: 50px 0;

    @include mq(ipadP) {
      margin-top: $margin-top;
      margin-bottom: unset;
      padding: 0;
    }
  }

    &__grid {
      padding: 0;
    }

  &__wrapper {
    line-height: 0;
    padding: $horizontal-padding-mobile-s $horizontal-padding-mobile-s 0;

    @include mq(mobileX) {
      padding: $horizontal-padding-mobile $horizontal-padding-mobile 0;
    }

    @include mq(mobileL) {
      margin: 0 auto;
      padding: 0;
      max-width: 65vw;
    }
  }

  &__description {
    margin-bottom: 120px;

    @include mq(ipadP) {
      margin-top: 0;
      padding: 0;
    }

    h2 {
      margin: 0 auto;
      max-width: 29em;

      @include mq(mobileL) {
        letter-spacing: -1px;
      }
    }
  }

  &__main-img {
    display: block;
    position: relative;
    margin: $margin-top-mobile 0;
    padding-top: 80%;
    width: calc(100% + #{$horizontal-padding-mobile-s} * 2);

    background-color: rgba(255, 255, 255, 0.7);

    overflow: hidden;
    transform: translateX(-$horizontal-padding-mobile-s);

    @include mq(mobileX) {
      width: calc(100% + #{$horizontal-padding-mobile} * 2);
      transform: translateX(-$horizontal-padding-mobile);
    }

    @include mq(ipadL) {
      margin-top: $margin-top;
      margin-bottom: $margin-top;
      padding-top: 50%;
      width: 100%;

      transform: none;
    }

    & img {
      top: 0;
      left: -50%;
      position: absolute;
      width: auto;
      height: 140%;

      will-change: transform;
      // backface-visibility: hidden;

      @include mq(ipadP) {
        left: -10%;
        height: 145%;
      }

      @include mq(ipadL) {
        left: 0;
        width: 100%;
      }
    }
  }

  &__contact {
    margin: $margin-top 0;
    width: calc(100% + #{$horizontal-padding-mobile-s} * 2);
    transform: translateX(-$horizontal-padding-mobile-s);

    @include mq(mobileX) {
      width: calc(100% + #{$horizontal-padding-mobile} * 2);
      transform: translateX(-$horizontal-padding-mobile);
    }

    @include mq(ds) {
      margin: $horizontal-padding-mobile-s 0;
    }
  }

  &__next {
    padding-bottom: 0;
  }
}
</style>
